import React, { useState, useEffect, useRef } from 'react';
import axios from "axios";
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { performLogout } from '../../utils/logoutHandler';
import { useSessionGuard, ExistingSessionModal, SessionConflictModal, SINGLE_SESSION_ROLES, getDashboardRouteForRole, getBrowserName } from './SessionGuard';
import { SUPPORT_EMAIL } from '../../config/supportContacts';
const API_URL = process.env.REACT_APP_API_URL;

const LANGUAGE_OPTIONS = [
  { code: 'en', labelKey: 'lang.en', defaultValue: 'English' },
  { code: 'hi', labelKey: 'lang.hi', defaultValue: 'हिन्दी' },
  { code: 'ar', labelKey: 'lang.ar', defaultValue: 'العربية' },
];

const CaraLoginPage = () => {
  const { t, i18n } = useTranslation(['cara_login', 'common']);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const languageMenuRef = useRef(null);
  const currentLanguage = LANGUAGE_OPTIONS.find((l) => l.code === i18n.language) || LANGUAGE_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectLanguage = (code) => {
    localStorage.setItem('cara_lang', code);
    i18n.changeLanguage(code);
    setShowLanguageMenu(false);
  };

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(""); // State for success/error message
  const [ipAddress, setIpAddress] = useState(""); // State to store IP address
  const [resetmessage, setResetMessage] = useState(""); // State for success/error message
  const [resetloading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0); // Track failed login attempts
  const [showDeactivatedModal, setShowDeactivatedModal] = useState(false); // Control deactivated modal visibility

  const {
    existingSession,
    showExistingSessionModal,
    setShowExistingSessionModal,
    error,
    setError,
    showErrorModal,
    showLogoutButton,
    setShowLogoutButton,
    handleSessionConflict,
    logoutExistingSession,
    closeErrorModal,
  } = useSessionGuard(SINGLE_SESSION_ROLES);

  const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/i.test(userAgent)) return "Mobile";
    if (/tablet|ipad/i.test(userAgent)) return "Tablet";
    return "Desktop";
  };
  const { caraUser } = useAuth();

  // Fetch public IPv4 address on component mount
  useEffect(() => {
    axios.get("https://api4.ipify.org?format=json")
      .then(response => setIpAddress(response.data.ip))
      .catch(error => console.error("Error fetching IP:", error));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setShowLogoutButton(false); // Reset logout button state on new attempt
    setLoading(true); // Set loading to true when starting the login request

    try {
      const response = await axios.post(`${API_URL}api/user_login/`, {
        username,
        password,
        ip_address: ipAddress,
        device_type: getDeviceType(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

      }, { withCredentials: true }); // Allows Django session to be stored
      console.log(ipAddress)


      if (response.status === 200) {
        // Reset failed attempts on successful login
        setFailedAttempts(0);

        // Store the username in localStorage after successful login
        localStorage.setItem("username", response.data.username);
        localStorage.setItem("user_id", response.data.user_id);
        localStorage.setItem("role", response.data.role);
        localStorage.setItem("email", response.data.email || "");
        localStorage.setItem("first_name", response.data.first_name || "");
        localStorage.setItem("last_name", response.data.last_name || "");
        localStorage.setItem("device_type", getDeviceType());
        localStorage.setItem("browser", getBrowserName());

        // Check if OTP bypass is enabled
        if (response.data.requires_2fa === false || response.data.requires_2fa === "false") {
          // OTP bypass enabled - show OTP page for 2 seconds then auto-redirect
          console.log(" Login Code Bypass enabled - redirecting to Login Code page for auto-verification");
          setMessage(t('msg.loginSuccessVerifyingOtp', { defaultValue: 'Login successful! Verifying OTP...' }));

          // Store bypass flag in localStorage
          localStorage.setItem("otp_bypass_mode", "true");

          // Redirect to OTP page (will auto-verify and redirect after 2 seconds)
          setTimeout(() => {
            window.location.replace(`/otp?user_type=CARA`);
          }, 1000);

        } else {
          // Normal OTP flow
          console.log(" Login Code required - redirecting to Login Code page");
          setMessage(t('msg.loginSuccessRedirecting', { defaultValue: 'Login successful! Redirecting...' }));

          // Redirect to OTP validation page
          setTimeout(() => {
            window.location.replace(`/otp?user_type=CARA`);
          }, 1000);
        }
      }
    } catch (error) {
      if (error.response) {
        // Handle 403 - User already logged in from another location
        if (handleSessionConflict(error)) {
          return;
        }

        // Backend returned an error (400/500)
        const errorMessage = error.response.data.message || t('msg.invalidCredentials', { defaultValue: 'Invalid credentials. Please try again.' });
        setShowLogoutButton(false);

        if (error.response.data.status === "DEACTIVATED") {
          setShowDeactivatedModal(true);
          setLoading(false);
          return;
        }

        setError(errorMessage);

        // Increment failed attempts
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);

        // Show alert after 3 failed attempts
        if (newFailedAttempts >= 3) {
          alert(t('msg.forgotPasswordHint', { defaultValue: 'Forgot your password? Click the "Forgot Password" button to reset it.' }));
          setFailedAttempts(0); // Reset counter after showing alert
        }
      } else {
        // Network or unexpected error
        setError(t('msg.unexpectedError', { defaultValue: 'An unexpected error occurred. Please try again.' }));
      }
    } finally {
      setLoading(false); // Set loading to false when the request finishes
    }
  };

  const handleForgotPassword = async () => {
    setResetLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API_URL}api/forgot_password/`, {
        username: username
      }, { withCredentials: true });

      setResetMessage(response.data.resetmessage);
      alert(t('msg.resetLinkSent', { defaultValue: 'Reset Password link has been sent to your email ID.' }));
      setUsername("");  // clear input
      window.location.reload();
    } catch (err) {
      if (err.response?.data?.status === "DEACTIVATED") {
        setShowDeactivatedModal(true);
        return;
      }
      setError(err.response?.data?.message || t('msg.somethingWentWrong', { defaultValue: 'Something went wrong!' }));
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Get username and role from localStorage to update statistics
      const username = localStorage.getItem('username');
      const role = localStorage.getItem('role'); // CARA role for this page

      // Call backend logout API first
      console.log('📞 Calling backend logout API...');
      await axios.post(`${API_URL}api/logout/`, { username }, { withCredentials: true });
      console.log('Backend logout API successful');

      // Use centralized logout handler for cross-tab synchronization
      // This is CARA login page, so redirect to CARA login
      await performLogout('CARA', null, true);

    } catch (err) {
      console.warn("Logout error:", err);
      // Even if logout API fails, use centralized logout
      await performLogout('CARA', null, true);
    }
  };

  const handleLogoutExistingSession = async () => {
    // Invalidates the existing server session, clears client-side cache,
    // then lets the user continue with a fresh login attempt.
    const result = await logoutExistingSession(username);
    if (result.success) {
      setMessage(result.message);
      setPassword("");
    }
  };

  const handleContinueWithExistingSession = () => {
    setShowExistingSessionModal(false);
    // Redirect to the existing session's own role's destination, whichever
    // of the 4 core portals it actually belongs to.
    window.location.replace(getDashboardRouteForRole(existingSession?.role));
  };
  return (
    <div className="">
      <main id="user_login">
        <div className="d-flex align-items-center justify-content-center p-2" style={{ minHeight: '100vh', backgroundColor: '#e2e8f0' }}>
          <div className="card shadow-lg border-0 w-100" style={{ maxWidth: '1200px', borderRadius: '16px', overflow: 'hidden' }}>
            <div className="row g-0" style={{ minHeight: '85vh' }}>
              {/* Left Section (Marketing) */}
              <div className="col-md-7 d-flex flex-column p-5 position-relative" style={{ backgroundColor: '#f4f9ff' }}>
            {/* Background decorative elements */}
            <div style={{
              position: 'absolute', top: '-10%', right: '-10%', width: '60%', height: '60%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(244,249,255,0) 70%)',
              borderRadius: '50%', zIndex: 0
            }}></div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', background: 'linear-gradient(to top, rgba(13,110,253,0.1), transparent)', zIndex: 0 }}></div>
            
            <div style={{ zIndex: 1 }} className="d-flex flex-column h-100 justify-content-between">
              <div>
                {/* Logo Area */}
                <div className="mb-4 d-flex align-items-center">
                  <i className="fas fa-shield-alt text-primary fs-2 me-2"></i>
                  <h2 className="mb-0 fw-bold" style={{ color: '#0f172a' }}>CARA<span style={{ fontWeight: 'normal', color: '#475569' }}>grc</span></h2>
                </div>

                {/* Headings */}
                <h1 className="fw-bold mb-1" style={{ color: '#1e293b', fontSize: '2.5rem' }}>
                  {t('title.stopChasingCompliance', { defaultValue: 'Stop Chasing Compliance.' })}
                </h1>
                <h1 className="fw-bold mb-3" style={{ color: '#0d6efd', fontSize: '2.5rem' }}>
                  {t('title.startManagingIt', { defaultValue: 'Start Managing It.' })}
                </h1>
                <div style={{ width: '40px', height: '4px', backgroundColor: '#0d6efd', borderRadius: '2px', marginBottom: '1.5rem' }}></div>
                
                <p className="fs-5 mb-4" style={{ color: '#475569', maxWidth: '80%' }}>
                  {t('desc.bringScatteredEvidence', { defaultValue: 'Bring scattered evidence, overdue actions, risks, audits, vendors and compliance requirements into one connected workspace.' })}
                </p>

                {/* Info Box */}
                <div className="p-3 mb-4 rounded-4 d-flex align-items-center" style={{ backgroundColor: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', maxWidth: '85%' }}>
                  <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: '60px', height: '60px', minWidth: '60px' }}>
                    <i className="fas fa-shield-alt text-white fs-4"></i>
                  </div>
                  <p className="mb-0 fw-semibold" style={{ color: '#0f172a' }}>
                    {t('desc.logInToSee', { defaultValue: 'Log in to see what needs attention, what is overdue and what could expose your organization to risk.' })}
                  </p>
                </div>

                {/* Icons row */}
                <div className="row text-center mt-auto" style={{ maxWidth: '90%' }}>
                  <div className="col-3">
                    <div className="mb-2"><i className="fas fa-shield-alt fs-3 text-primary"></i></div>
                    <small className="fw-semibold" style={{ color: '#475569' }}>{t('feature.identifyRisksEarly', { defaultValue: 'Identify Risks Early' })}</small>
                  </div>
                  <div className="col-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
                    <div className="mb-2"><i className="fas fa-tasks fs-3 text-primary"></i></div>
                    <small className="fw-semibold" style={{ color: '#475569' }}>{t('feature.closeGapsFaster', { defaultValue: 'Close Gaps Faster' })}</small>
                  </div>
                  <div className="col-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
                    <div className="mb-2"><i className="fas fa-file-signature fs-3 text-primary"></i></div>
                    <small className="fw-semibold" style={{ color: '#475569' }}>{t('feature.stayAuditReady', { defaultValue: 'Stay Audit Ready' })}</small>
                  </div>
                  <div className="col-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
                    <div className="mb-2"><i className="fas fa-chart-line fs-3 text-primary"></i></div>
                    <small className="fw-semibold" style={{ color: '#475569' }}>{t('feature.driveConfidentDecisions', { defaultValue: 'Drive Confident Decisions' })}</small>
                  </div>
                </div>
              </div>



            </div>
          </div>

          {/* Right Section (Login Form) */}
          <div className="col-md-5 d-flex align-items-center justify-content-center p-5" style={{ backgroundColor: '#f1f5f9' }}>
            <div className="card border-0 w-100" style={{ maxWidth: '450px', borderRadius: '16px', backgroundColor: '#ffffff', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' }}>
              <div className="card-body p-5">
                <h3 className="fw-bold text-center mb-2" style={{ color: '#0f172a' }}>{t('title.userLogin', { defaultValue: 'CARAgrc User Login' })}</h3>
                <p className="text-center text-muted mb-3">{t('title.signInToContinue', { defaultValue: 'Sign in to continue to CARAgrc' })}</p>

                <form onSubmit={handleLogin}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small">{t('label.usernameOrEmail', { defaultValue: 'Username or Email Address' })}</label>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control form-control-lg bg-light"
                        style={{ paddingLeft: '2.8rem', fontSize: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        placeholder={t('placeholder.usernameOrEmail', { defaultValue: 'Enter your username or email' })}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                      />
                      <i className="far fa-user text-muted position-absolute" style={{ left: '1rem', top: '50%', transform: 'translateY(-50%)' }}></i>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold text-muted small">{t('label.password', { defaultValue: 'Password' })}</label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control form-control-lg bg-light"
                        style={{ paddingLeft: '2.8rem', paddingRight: '2.5rem', fontSize: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        placeholder={t('placeholder.enterYourPassword', { defaultValue: 'Enter your password' })}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                      <i className="fas fa-lock text-muted position-absolute" style={{ left: '1rem', top: '50%', transform: 'translateY(-50%)' }}></i>
                      <i
                        className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted position-absolute`}
                        style={{ right: '1rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                        onClick={() => setShowPassword(!showPassword)}
                      ></i>
                    </div>
                  </div>

                  {error && <p className="text-danger mt-2 mb-0 small text-center">{error}</p>}
                  
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div></div>
                    <a href="#" className="text-decoration-none small fw-semibold" style={{ color: '#0d6efd' }} data-bs-toggle="modal" data-bs-target="#forgotPasswordModal">
                      Forgot password?
                    </a>
                  </div>

                  <button type="submit" className="btn btn-primary w-100 py-2 mb-3 fw-semibold" style={{ borderRadius: '8px', backgroundColor: '#0d6efd' }} disabled={loading}>
                    {loading ? t('btn.signingIn', { defaultValue: 'Signing In...' }) : t('btn.signIn', { defaultValue: 'Sign In' })}
                  </button>
                  {message && <p className="mt-2 text-success text-center small">{message}</p>}

                </form>
                
                <div className="d-flex justify-content-between align-items-center mt-4 pt-2">
                  {/* Language Selector */}
                  <div className="position-relative d-inline-block" ref={languageMenuRef}>
                    <button
                      type="button"
                      className="btn d-flex align-items-center gap-1 bg-white border"
                      onClick={() => setShowLanguageMenu((prev) => !prev)}
                      aria-haspopup="listbox"
                      aria-expanded={showLanguageMenu}
                      style={{
                        color: '#475569',
                        borderRadius: '8px',
                        padding: '4px 10px',
                        fontWeight: 600,
                        fontSize: '0.85rem'
                      }}
                    >
                      <i className="fas fa-globe me-1"></i>
                      {t(currentLanguage.labelKey, { ns: 'common', defaultValue: currentLanguage.defaultValue })}
                      <i className={`fas fa-chevron-${showLanguageMenu ? 'up' : 'down'} ms-1`} style={{ fontSize: '0.6rem' }}></i>
                    </button>
                    {showLanguageMenu && (
                      <div
                        role="listbox"
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          top: 'auto',
                          left: 0,
                          marginBottom: '0.5rem',
                          minWidth: '120px',
                          backgroundColor: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                          padding: '4px',
                          zIndex: 1050,
                        }}
                      >
                        {LANGUAGE_OPTIONS.map((option) => {
                          const isActive = i18n.language === option.code;
                          return (
                            <button
                              type="button"
                              key={option.code}
                              onClick={() => handleSelectLanguage(option.code)}
                              style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                marginBottom: '2px',
                                fontWeight: isActive ? 700 : 500,
                                fontSize: '0.85rem',
                                color: isActive ? '#fff' : '#1f2937',
                                background: isActive ? '#0d6efd' : 'transparent',
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                            >
                              {t(option.labelKey, { ns: 'common', defaultValue: option.defaultValue })}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <span className="text-muted small">{t('text.needHelp', { defaultValue: 'Need help? ' })}</span>
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-decoration-none small fw-semibold" style={{ color: '#0d6efd' }}>{t('btn.contactSupport', { defaultValue: 'Contact Support' })}</a>
                  </div>
                </div>

              </div>
            </div>

            {/* Forgot Password Modal */}
            <div className="modal fade" id="forgotPasswordModal" tabIndex="-1" aria-labelledby="forgotPasswordLabel" aria-hidden="true">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
                  <div className="modal-header border-bottom-0 pb-0">
                    <h5 className="modal-title fw-bold" id="forgotPasswordLabel">{t('modal.forgotPassword.title', { defaultValue: 'Forgot Password?' })}</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div className="modal-body text-center pt-2 pb-4">
                    <p className="text-muted small mb-4">{t('modal.forgotPassword.desc', { defaultValue: 'Enter your username or email address to receive a password reset link.' })}</p>
                    <div className="form-group mb-3">
                      <input
                        type="text"
                        className="form-control form-control-lg bg-light"
                        placeholder={t('placeholder.enterYourUsernameOrEmail', { defaultValue: 'Enter your username or email' })}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        style={{ borderRadius: '8px' }} 
                      />
                    </div>
                    {error && <p className="text-danger mt-2 mb-0 small">{error}</p>}
                    {resetmessage && <p className="text-success mt-2 mb-0 small">{resetmessage}</p>}
                    <button
                      type="button"
                      className="btn btn-primary w-100 py-2 mt-3 fw-semibold"
                      onClick={handleForgotPassword}
                      disabled={resetloading}
                      style={{ borderRadius: '8px' }}
                    >
                      {resetloading ? t('btn.sending', { defaultValue: 'Sending...' }) : t('btn.sendResetLink', { defaultValue: 'Send Reset Link' })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Existing Session Modal */}
      <ExistingSessionModal
        show={showExistingSessionModal}
        existingSession={existingSession}
        onLogout={handleLogout}
        onContinue={handleContinueWithExistingSession}
      />

      {/* Session Conflict / Login Error Modal */}
      <SessionConflictModal
        show={showErrorModal}
        error={error}
        showLogoutButton={showLogoutButton}
        onClose={closeErrorModal}
        onLogoutExisting={handleLogoutExistingSession}
      />

      {/* Deactivated Modal */}
      {showDeactivatedModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
              <div className="modal-header bg-danger text-white" style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}>
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-person-x-fill me-2"></i>
                  {t('modal.deactivated.title', { defaultValue: 'Account Deactivated' })}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDeactivatedModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4 text-center">
                <div className="mb-4">
                  <i className="bi bi-exclamation-octagon text-danger" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="fw-bold mb-3">{t('modal.deactivated.heading', { defaultValue: 'Login Restricted' })}</h4>
                <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
                  {t('modal.deactivated.messagePrefix', { defaultValue: 'Your account has been' })} <strong>{t('modal.deactivated.deactivatedWord', { defaultValue: 'deactivated' })}</strong>. {t('modal.deactivated.messageSuffix', { defaultValue: 'You cannot login at this time.' })}
                </p>
                <div className="bg-light p-3 rounded-4 mb-4">
                  <p className="mb-0 text-dark">
                    {t('modal.deactivated.contactPrefix', { defaultValue: 'Please contact our support team at' })} <strong>{SUPPORT_EMAIL}</strong> {t('modal.deactivated.contactSuffix', { defaultValue: 'or your administrator to reactivate your account.' })}
                  </p>
                </div>
              </div>
              <div className="modal-footer bg-light p-3" style={{ borderBottomLeftRadius: '15px', borderBottomRightRadius: '15px' }}>
                <button
                  type="button"
                  className="btn btn-secondary px-4 shadow-sm"
                  onClick={() => setShowDeactivatedModal(false)}
                >
                  {t('btn.close', { ns: 'common', defaultValue: 'Close' })}
                </button>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="btn btn-danger px-4 shadow-sm">
                  {t('btn.contactSupport', { defaultValue: 'Contact Support' })}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivated Modal Backdrop */}
      {showDeactivatedModal && (
        <div className="modal-backdrop fade show"></div>
      )}

    </div>
  );
};

export default CaraLoginPage